import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { formatDayText } from '../events/fields';
import { trapTab } from '../ui/focus';
import { PAYMENTS_KEY, type Receivable, type ReviewItem } from './types';
import { DialogLayer } from '../ui/Dialog';
import { useUnsavedGuard } from '../ui/unsaved';

type Row = { id: string; kind: 'receivableId' | 'payableId'; label: string; who: string; remainingGrosze: number | undefined; suggested: boolean };

/**
 * "Podziel / inna należność…" (F38): one bank operation booked on one or several receivables (money in) or on the
 * suggested payables (money out). The suggested ones come first; the sum may not exceed the operation and no part
 * what is left to pay (the server checks both: 422 `allocation_exceeds`).
 */
export function SplitDialog({ item, onClose, onSaved }: { item: ReviewItem; onClose: () => void; onSaved: () => void }) {
  // a payable or refund suggestion means money out (also for whoever does not see the amount)
  const incoming = item.transaction.amountGrosze === undefined ? item.suggestions.every((s) => !s.payable && !s.refund) : item.transaction.amountGrosze > 0;
  const open = useQuery({
    queryKey: [...PAYMENTS_KEY, 'list', 'otwarte', ''],
    queryFn: () => api<{ receivables: Receivable[] }>('/api/payments/receivables?state=otwarte'),
    enabled: incoming,
  });
  const [q, setQ] = useState('');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [local, setLocal] = useState('');
  const save = useMutation({
    mutationFn: (parts: Array<Record<string, unknown>>) => api(`/api/payments/transactions/${item.transaction.id}/allocate`, { method: 'POST', body: { parts } }),
    onSuccess: onSaved,
  });

  const suggestedIds = new Set(item.suggestions.map((s) => s.receivable?.id ?? s.payable?.id));
  const rows: Row[] = incoming
    ? [...(open.data?.receivables ?? [])]
        .sort((a, b) => Number(suggestedIds.has(b.id)) - Number(suggestedIds.has(a.id)))
        .map((r) => ({ id: r.id, kind: 'receivableId', label: r.label, who: [r.contractorName, r.eventName].filter(Boolean).join(' · '), remainingGrosze: r.remainingGrosze, suggested: suggestedIds.has(r.id) }))
    : item.suggestions.flatMap((s): Row[] => {
        if (s.payable) return [{ id: s.payable.id, kind: 'payableId', label: s.payable.label, who: `termin ${formatDayText(s.payable.dueDate)}`, remainingGrosze: s.payable.remainingGrosze, suggested: true }];
        // a refund of what the client overpaid (F40 "Do zwrotu")
        if (s.receivable && s.refund) return [{ id: s.receivable.id, kind: 'receivableId', label: `Zwrot: ${s.receivable.label}`, who: s.receivable.contractorName, remainingGrosze: s.receivable.refundGrosze, suggested: true }];
        return [];
      });
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = rows.filter((r) => words.every((w) => `${r.label} ${r.who}`.toLowerCase().includes(w)) || amounts[r.id]);

  let total = 0;
  for (const value of Object.values(amounts)) {
    try {
      if (value.trim()) total += parsePln(value);
    } catch {
      /* shown on saving */
    }
  }
  // what is left of the operation to allocate (a partly allocated one: its rest)
  const operation = item.unallocatedGrosze ?? (item.transaction.amountGrosze === undefined ? undefined : Math.abs(item.transaction.amountGrosze));

  /** What to send, or null with the reason shown. */
  function payload() {
    const fail = (why: string) => (setLocal(why), null);
    const parts: Array<Record<string, unknown>> = [];
    for (const r of rows) {
      const value = amounts[r.id]?.trim();
      if (!value) continue;
      let amountGrosze: number;
      try {
        amountGrosze = parsePln(value);
      } catch (error) {
        return fail(`${r.label}: ${error instanceof Error ? error.message : 'nieprawidłowa kwota'}`);
      }
      if (amountGrosze <= 0) return fail(`${r.label}: kwota musi być większa od zera`);
      parts.push({ [r.kind]: r.id, amountGrosze });
    }
    if (parts.length === 0) return fail('Wpisz kwotę przy co najmniej jednej pozycji');
    if (operation !== undefined && total > operation) return fail('Suma części przekracza to, co zostało z operacji');
    setLocal('');
    return parts;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const sent = payload();
    if (sent !== null) save.mutate(sent);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: Object.values(amounts).some((v) => v.trim() !== ''),
    save: async () => {
      const sent = payload();
      if (sent === null) return false;
      await save.mutateAsync(sent);
      return true;
    },
  });
  const error = save.error instanceof ApiError ? (fieldIssues(save.error)[0]?.message ?? save.error.message) : save.error ? 'Nie udało się zapisać' : '';
  return (
    <div className="dialog-backdrop">
      <form
        ref={guardRef}
        className="dialog panel form pay-split"
        role="dialog"
        aria-modal="true"
        aria-labelledby="split-title"
        data-feature-id="payments.review"
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={save.isPending} />
        <h2 id="split-title">{incoming ? 'Przydziel wpłatę' : 'Przydziel wydatek'}</h2>
        <p className="muted">
          {formatDayText(item.transaction.bookingDate)} · {item.transaction.counterpartyName || 'bez nazwy'}
          {operation !== undefined ? ` · ${formatPln(operation)}` : ''}
        </p>
        {incoming && <input type="search" aria-label="Szukaj należności" placeholder="Klient, wydarzenie, numer faktury" value={q} onChange={(e) => setQ(e.target.value)} />}
        {open.error && <p className="alert" role="alert">{open.error instanceof ApiError ? open.error.message : 'Nie udało się wczytać należności'}</p>}
        {shown.length === 0 && (open.data || !incoming) && <p className="muted">{incoming ? 'Brak otwartych należności.' : 'Brak zobowiązań do wyboru.'}</p>}
        {shown.length > 0 && (
          <div className="table-wrap pay-split-list">
            <table className="table pay-table" aria-label={incoming ? 'Należności do wyboru' : 'Zobowiązania do wyboru'}>
              <thead>
                <tr>
                  <th scope="col">{incoming ? 'Należność' : 'Zobowiązanie'}</th>
                  <th scope="col" className="num">
                    {incoming ? 'Pozostało' : 'Do zapłaty / zwrotu'}
                  </th>
                  <th scope="col" className="num">
                    Kwota (zł)
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.slice(0, 100).map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.label}
                      {r.suggested && <span className="pay-tag">propozycja</span>}
                      <span className="pay-sub">{r.who}</span>
                    </td>
                    <td className="num nowrap">{r.remainingGrosze === undefined ? '—' : formatPln(r.remainingGrosze)}</td>
                    <td className="num">
                      <input
                        className="num pay-part"
                        inputMode="decimal"
                        aria-label={`Kwota dla ${r.label}`}
                        value={amounts[r.id] ?? ''}
                        onChange={(e) => setAmounts({ ...amounts, [r.id]: e.target.value })}
                        onBlur={() => {
                          const v = amounts[r.id];
                          if (!v?.trim()) return;
                          try {
                            setAmounts({ ...amounts, [r.id]: plnToInput(parsePln(v)) });
                          } catch {
                            /* the message comes on saving */
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="pay-split-sum">
          Przydzielono <strong>{formatPln(total)}</strong>
          {operation !== undefined && <> z {formatPln(operation)}</>}
        </p>
        {(local || error) && (
          <p className="alert" role="alert">
            {local || error}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : 'Przydziel'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
